import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { notifySuccess } from '@/utils/notify';

const STORAGE_KEY = 'activeUnitId';

export interface UnidadeEscolar {
    id_unidade: string;
    nome: string;
}

interface ActiveUnitContextType {
    activeUnitId: string | null;
    activeUnitName: string | null;
    units: UnidadeEscolar[];
    isLoading: boolean;
    setActiveUnit: (id: string) => void;
}

const ActiveUnitContext = createContext<ActiveUnitContextType | undefined>(undefined);

export const ActiveUnitProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, roles, loading: authLoading } = useAuth();
    const queryClient = useQueryClient();
    const [units, setUnits] = useState<UnidadeEscolar[]>([]);
    const [activeUnitId, setActiveUnitId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const prevUnitRef = useRef<string | null>(null);

    const isAdminOuTi = roles.includes('admin') || roles.includes('ti');

    // Busca unidades do usuário
    useEffect(() => {
        if (authLoading || !user) {
            setIsLoading(false);
            return;
        }

        const fetchUnidades = async () => {
            setIsLoading(true);
            try {
                let data: UnidadeEscolar[];

                if (isAdminOuTi) {
                    const { data: todas, error } = await supabase
                        .from('unidade_escolar')
                        .select('id_unidade, nome')
                        .order('nome');
                    if (error) throw error;
                    data = (todas ?? []) as UnidadeEscolar[];
                } else {
                    const { data: vinculos, error } = await supabase
                        .from('usuario_unidades')
                        .select('id_unidade, unidade_escolar:id_unidade ( id_unidade, nome )')
                        .eq('user_id', user.id)
                        .is('deleted_at', null);
                    if (error) throw error;
                    data = (vinculos ?? [])
                        .map((v: any) => v.unidade_escolar)
                        .filter(Boolean) as UnidadeEscolar[];
                }

                setUnits(data);

                // Auto-seleção
                const storedId = localStorage.getItem(STORAGE_KEY);
                if (data.length === 1) {
                    setActiveUnitId(data[0].id_unidade);
                    localStorage.setItem(STORAGE_KEY, data[0].id_unidade);
                } else if (storedId && data.some((u) => u.id_unidade === storedId)) {
                    setActiveUnitId(storedId);
                } else {
                    setActiveUnitId(null);
                }
            } catch (err) {
                console.error('[ActiveUnitContext] Erro ao buscar unidades:', err);
                setUnits([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUnidades();
    }, [user, authLoading, isAdminOuTi]);

    const setActiveUnit = useCallback((id: string) => {
        const prevId = prevUnitRef.current;
        setActiveUnitId(id);
        localStorage.setItem(STORAGE_KEY, id);
        const nome = units.find((u) => u.id_unidade === id)?.nome;
        notifySuccess(`Unidade ativa: ${nome ?? id}`);

        // Reset controlado: remove cache de dados antigos da unidade anterior
        // e invalida apenas queries dependentes de unidade (não globais)
        queryClient.removeQueries({
            predicate: (query) => {
                const key = query.queryKey;
                // Preserva dados globais (tabelas de referência)
                if (Array.isArray(key) && key[0] === 'global') return false;
                // Preserva dados que não dependem de unidade (professores, cursos, disciplinas)
                if (Array.isArray(key) && typeof key[0] === 'string') {
                    const independentes = ['professores', 'cursos_modulo', 'disciplinas_modulo'];
                    if (independentes.includes(key[0])) return false;
                }
                return true;
            },
        });

        // Força refetch dos dados da nova unidade
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey;
                if (Array.isArray(key) && key[0] === 'global') return false;
                return true;
            },
        });

        prevUnitRef.current = id;
    }, [units, queryClient]);

    // Sync ref
    useEffect(() => {
        prevUnitRef.current = activeUnitId;
    }, [activeUnitId]);

    const activeUnitName = units.find((u) => u.id_unidade === activeUnitId)?.nome ?? null;

    return (
        <ActiveUnitContext.Provider value={{ activeUnitId, activeUnitName, units, isLoading, setActiveUnit }}>
            {children}
        </ActiveUnitContext.Provider>
    );
};

export const useActiveUnit = () => {
    const context = useContext(ActiveUnitContext);
    if (!context) {
        return {
            activeUnitId: null,
            activeUnitName: null,
            units: [] as UnidadeEscolar[],
            isLoading: true,
            setActiveUnit: () => {},
        } as ActiveUnitContextType;
    }
    return context;
};

/**
 * Fase 4 — Seletores específicos.
 * Evitam re-render de componentes que só consomem id/nome quando outros campos
 * do contexto mudam (lista de units, isLoading, etc.).
 */
export const useActiveUnitId = (): string | null => useActiveUnit().activeUnitId;
export const useActiveUnitName = (): string | null => useActiveUnit().activeUnitName;
